import React, { useEffect, useMemo, useRef, useState } from "react";
import YouTube from "react-youtube";
import {
  Crosshair, Check, X as XIcon, RotateCcw, ChevronLeft, ChevronRight, Save, Trash2,
  Activity, Eye, EyeOff, Ruler, ShieldCheck, Skull, ArrowRightCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { fehlerText, api as axiosApi } from "@/lib/api";
import { FIELD_PRESETS, getPreset } from "@/lib/fieldPresets";

const STEPS = [
  { idx: 0, label: "Top-Left", short: "TL" },
  { idx: 1, label: "Top-Right", short: "TR" },
  { idx: 2, label: "Bottom-Right", short: "BR" },
  { idx: 3, label: "Bottom-Left", short: "BL" },
];

// YouTube tactics don't carry a game_config, so default to Outdoor — the
// vast majority of curated content. If we ever add per-tactic format, replace
// this lookup accordingly.
const TACTIC_FIELD_PRESETS = FIELD_PRESETS.Outdoor;

/**
 * Field calibration overlay for a single YouTube tactic.
 *
 * Pattern mirrors Step2Calibration from the upload pipeline, but adapted for
 * a YouTube iframe (no pixel access). Workflow:
 *   1. User scrubs to a clear frame via the YT player or ±1s / ±0.1s buttons
 *   2. Locks frame → iframe receives `pointer-events: none`, overlay captures clicks
 *   3. User clicks 4 corners in TL → TR → BR → BL order; numbered markers stay
 *      visible. Order is enforced by the wizard.
 *   4. Backend validates convex quadrilateral & runs cv2.getPerspectiveTransform.
 */
export default function TacticCalibrator({ tactic, onSaved }) {
  const playerRef = useRef(null);
  const overlayRef = useRef(null);

  const [phase, setPhase] = useState("idle");      // idle | placing | depth_near | depth_far | saving | vector | pass_vector
  const [frameT, setFrameT] = useState(tactic.calibration?.calibration_frame_timestamp ?? tactic.start_seconds);
  const [fieldType, setFieldType] = useState(tactic.calibration?.field_type ?? "hauptspielfeld");
  const [points, setPoints] = useState(tactic.calibration?.image_points ?? []);
  const [overlaySize, setOverlaySize] = useState({ w: 0, h: 0 });
  // Vector Preview state — admin-only sanity-check: click pixels, see them
  // projected onto a top-down field map via the saved homography.
  const [vectorPoints, setVectorPoints] = useState([]); // [{img:{x,y}, field:{x,y}}]

  // ── Pass-vector (release → catch) — the Passing-Flow-Map signal. Two
  // pins on the calibrated frame, both in normalised [0..1] image coords.
  // Hydrate from a previously-saved tactic so re-opening shows the arrow.
  const _hydPV = tactic.calibration?.pass_vector;
  const [passVecStart, setPassVecStart] = useState(_hydPV?.start_image_xy ? { ..._hydPV.start_image_xy } : null);
  const [passVecEnd, setPassVecEnd] = useState(_hydPV?.end_image_xy ? { ..._hydPV.end_image_xy } : null);
  // Saving in-flight (so we can disable controls and show a spinner).
  const [passVecSaving, setPassVecSaving] = useState(false);

  // Optional depth-line anchors (Phase 2/3 in the master prompt). Each line
  // = 2 normalized image points + a real-world meter length. The first point
  // sits on the goal-line, the second on the back-line of the endzone.
  const _hydDepth = (side) => (tactic.calibration?.depth_lines || []).find((d) => (d.side || "near") === side);
  const _hNear = _hydDepth("near"); const _hFar = _hydDepth("far");
  const [nearLine, setNearLine] = useState(_hNear?.image_points || []);
  const [nearMeters, setNearMeters] = useState(_hNear?.meters || 18.0);
  const [farLine, setFarLine] = useState(_hFar?.image_points || []);
  const [farMeters, setFarMeters] = useState(_hFar?.meters || 18.0);
  const [brickMeters, setBrickMeters] = useState(tactic.calibration?.brick_distance_m ?? 20.0);
  const [brickSide, setBrickSide] = useState(tactic.calibration?.brick_side ?? "near");

  const onReady = (e) => { playerRef.current = e.target; try { e.target.seekTo(frameT, true); } catch (_) { /* iframe not ready */ } };

  const seekBy = (delta) => {
    const next = Math.max(tactic.start_seconds, Math.min(tactic.end_seconds, frameT + delta));
    setFrameT(next);
    try { playerRef.current?.pauseVideo(); playerRef.current?.seekTo(next, true); } catch (_) { /* not ready */ }
  };
  const captureCurrentTime = () => {
    const p = playerRef.current;
    if (!p) return;
    try {
      p.pauseVideo();
      const t = Number(p.getCurrentTime() || frameT);
      setFrameT(t);
    } catch (_) { /* not ready */ }
  };

  // Lock the frame: pause + freeze + activate overlay
  const lockFrame = () => {
    captureCurrentTime();
    setPhase("placing");
    setPoints([]);
  };
  const unlockFrame = () => {
    setPhase("idle");
  };

  // Track overlay rect (for normalising clicks)
  useEffect(() => {
    if (phase === "idle" || phase === "saving") return;
    const el = overlayRef.current;
    if (!el) return;
    const update = () => {
      const r = el.getBoundingClientRect();
      setOverlaySize({ w: r.width, h: r.height });
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [phase]);

  // ── Persist a freshly-placed pass-vector. Sends both pins to the backend
  // which projects them through H and stores start/end image_xy + field_xy
  // + length_m + angle_deg on the tactic. We pass the existing calibration
  // payload through unchanged so this becomes a pure additive update.
  const savePassVector = async (startXY, endXY) => {
    if (!startXY || !endXY) return;
    const cal = tactic.calibration;
    if (!cal) { toast.error("Calibrate the field first"); return; }
    setPassVecSaving(true);
    try {
      const updated = await axiosApi.post(`/tactics/${tactic.id}/calibrate`, {
        image_points: cal.image_points,
        calibration_frame_timestamp: cal.calibration_frame_timestamp,
        field_type: cal.field_type,
        depth_lines: cal.depth_lines || [],
        brick_distance_m: cal.brick_distance_m,
        brick_side: cal.brick_side,
        brick_image_xy: cal.brick_image_xy || undefined,
        pass_vector: { start: startXY, end: endXY },
      }).then((r) => r.data);
      const pv = updated?.calibration?.pass_vector;
      toast.success(
        pv?.length_m != null
          ? `Pass vector saved · ${pv.length_m.toFixed(1)} m`
          : "Pass vector saved",
      );
      onSaved?.(updated);
    } catch (err) {
      toast.error(fehlerText(err) || "Pass vector save failed");
    } finally {
      setPassVecSaving(false);
    }
  };

  const clearPassVector = async () => {
    setPassVecStart(null); setPassVecEnd(null);
    const cal = tactic.calibration;
    if (!cal?.pass_vector) return;       // nothing persisted yet — just local clear
    setPassVecSaving(true);
    try {
      // Re-POST without pass_vector so the backend drops it from the doc.
      const updated = await axiosApi.post(`/tactics/${tactic.id}/calibrate`, {
        image_points: cal.image_points,
        calibration_frame_timestamp: cal.calibration_frame_timestamp,
        field_type: cal.field_type,
        depth_lines: cal.depth_lines || [],
        brick_distance_m: cal.brick_distance_m,
        brick_side: cal.brick_side,
        brick_image_xy: cal.brick_image_xy || undefined,
      }).then((r) => r.data);
      toast.success("Pass vector cleared");
      onSaved?.(updated);
    } catch (err) {
      toast.error(fehlerText(err) || "Clear failed");
    } finally {
      setPassVecSaving(false);
    }
  };

  const handleOverlayClick = (ev) => {
    const r = overlayRef.current.getBoundingClientRect();
    const x = (ev.clientX - r.left) / r.width;
    const y = (ev.clientY - r.top) / r.height;
    if (x < 0 || x > 1 || y < 0 || y > 1) return;

    if (phase === "placing" && points.length < 4) {
      setPoints([...points, { x: round4(x), y: round4(y) }]);
      return;
    }
    if (phase === "depth_near" && nearLine.length < 2) {
      setNearLine([...nearLine, { x: round4(x), y: round4(y) }]);
      return;
    }
    if (phase === "depth_far" && farLine.length < 2) {
      setFarLine([...farLine, { x: round4(x), y: round4(y) }]);
      return;
    }
    if (phase === "vector" && tactic.calibration?.homography_matrix) {
      const field = applyHomography(tactic.calibration.homography_matrix, x, y);
      // Sanity-clip so far-out projection errors don't blow up the SVG.
      const W = tactic.calibration.rect_width_m || 37;
      const L = tactic.calibration.rect_length_m || 100;
      const ok = field.x >= -L * 0.5 && field.x <= L * 1.5 && field.y >= -W * 0.5 && field.y <= W * 1.5;
      setVectorPoints((prev) => [...prev, { img: { x: round4(x), y: round4(y) }, field, inside: ok }]);
      return;
    }
    if (phase === "pass_vector") {
      const px = { x: round4(x), y: round4(y) };
      if (!passVecStart) {
        // First click — release / werfer.
        setPassVecStart(px);
      } else if (!passVecEnd) {
        // Second click — catch / empfänger. Auto-save & persist.
        setPassVecEnd(px);
        savePassVector(passVecStart, px);
      } else {
        // Already complete → next click starts a fresh vector.
        setPassVecStart(px);
        setPassVecEnd(null);
      }
    }
  };
  const undoPoint = () => setPoints(points.slice(0, -1));
  const clearPoints = () => setPoints([]);
  const undoVector = () => setVectorPoints((prev) => prev.slice(0, -1));
  const clearVector = () => setVectorPoints([]);

  const canSave = points.length === 4 && !!fieldType;

  const onSave = async () => {
    if (!canSave) return;
    setPhase("saving");
    try {
      const depthLines = [];
      if (nearLine.length === 2 && nearMeters > 0) {
        depthLines.push({ image_points: nearLine, meters: nearMeters, side: "near", at_time_s: frameT });
      }
      if (farLine.length === 2 && farMeters > 0) {
        depthLines.push({ image_points: farLine, meters: farMeters, side: "far", at_time_s: frameT });
      }
      const updated = await axiosApi.post(`/tactics/${tactic.id}/calibrate`, {
        image_points: points,
        calibration_frame_timestamp: frameT,
        field_type: fieldType,
        depth_lines: depthLines,
        brick_distance_m: brickMeters,
        brick_side: brickSide,
      }).then((r) => r.data);
      const sc = updated?.calibration?.sanity_score;
      toast.success(`Calibration saved · ${getPreset("Outdoor", fieldType).labelDe}${sc != null ? ` · score ${sc}/100` : ""}`);
      onSaved?.(updated);
      setPhase("idle");
    } catch (err) {
      const msg = fehlerText(err) || err?.message || "Save failed";
      toast.error(typeof msg === "string" ? msg : "Calibration save failed");
      setPhase("placing");
    }
  };

  const onClearSaved = async () => {
    if (!confirm("Delete the saved calibration for this tactic?")) return;
    try {
      await axiosApi.delete(`/tactics/${tactic.id}/calibrate`);
      onSaved?.({ ...tactic, calibration: null });
      setPoints([]);
      toast.success("Calibration cleared");
    } catch (err) {
      toast.error(fehlerText(err) || "Clear failed");
    }
  };

  const currentStep = STEPS[points.length] || null;
  const hasSaved = !!tactic.calibration;

  return (
    <div className="border-t border-border bg-black/30" data-testid={`tactic-calibrator-${tactic.id}`}>
      {/* Header / status bar */}
      <div className="px-3 py-2 flex items-center justify-between gap-2 border-b border-border bg-card/40">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          <Crosshair className="w-3 h-3 text-primary" />
          Field Calibration · t={fmt(frameT)}
          {hasSaved && (
            <>
              <Badge className="ml-2 bg-primary/15 text-primary border border-primary/40 text-[9px] uppercase tracking-widest">
                saved · {getPreset("Outdoor", tactic.calibration.field_type).labelDe}
              </Badge>
              {tactic.calibration.sanity_score != null && (
                <Badge
                  data-testid={`tactic-sanity-badge-${tactic.id}`}
                  className={`text-[9px] uppercase tracking-widest border flex items-center gap-1 ${
                    tactic.calibration.sanity_score >= 70 ? "bg-emerald-400/15 text-emerald-300 border-emerald-400/40"
                    : tactic.calibration.sanity_score >= 30 ? "bg-amber-400/15 text-amber-300 border-amber-400/40"
                    : "bg-destructive/15 text-destructive border-destructive/40"
                  }`}>
                  {tactic.calibration.sanity_score >= 30 ? <ShieldCheck className="w-3 h-3" /> : <Skull className="w-3 h-3" />}
                  score {tactic.calibration.sanity_score}/100
                </Badge>
              )}
              {tactic.calibration.pass_vector?.length_m != null && (
                <Badge
                  data-testid={`tactic-pass-vector-badge-${tactic.id}`}
                  className="text-[9px] uppercase tracking-widest border flex items-center gap-1 bg-pink-400/15 text-pink-300 border-pink-400/40">
                  <ArrowRightCircle className="w-3 h-3" />
                  pass {tactic.calibration.pass_vector.length_m.toFixed(1)} m
                </Badge>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {hasSaved && phase === "idle" && (
            <Button size="sm" variant="ghost" onClick={onClearSaved}
              data-testid={`tactic-calib-clear-${tactic.id}`}
              className="h-7 px-2 hover:bg-destructive/20 hover:text-destructive font-mono text-[10px] uppercase tracking-widest">
              <Trash2 className="w-3 h-3 mr-1" /> Clear
            </Button>
          )}
        </div>
      </div>

      {/* Player + overlay */}
      <div className="relative aspect-video bg-black">
        <div className={(phase === "placing" || phase === "depth_near" || phase === "depth_far" || phase === "vector" || phase === "pass_vector") ? "pointer-events-none w-full h-full" : "w-full h-full"}>
          <YouTube
            videoId={tactic.youtube_id}
            onReady={onReady}
            opts={{
              width: "100%", height: "100%",
              playerVars: {
                modestbranding: 1, rel: 0,
                start: Math.floor(frameT),
                autoplay: 0, controls: (phase === "placing" || phase === "depth_near" || phase === "depth_far" || phase === "vector" || phase === "pass_vector") ? 0 : 1,
              },
            }}
            className="w-full h-full"
            iframeClassName="w-full h-full"
          />
        </div>

        {/* Draggable Brick reticle (shown when calibration saved + idle).
            Hidden if a pass_vector is persisted — the arrow becomes the
            canonical anchor and the brick dot would just clutter the frame. */}
        {phase === "idle" && hasSaved && tactic.calibration.brick_image_xy
          && !tactic.calibration.pass_vector && (
          <BrickReticleDrag
            tactic={tactic}
            onSaved={onSaved}
          />
        )}

        {/* Persisted pass-vector preview — visible in idle phase so admins
            see what's stored without entering edit mode. Click on the
            arrow overlay (via the controls) to re-place. */}
        {phase === "idle" && hasSaved && tactic.calibration?.pass_vector && (
          <PassVectorOverlay
            start={tactic.calibration.pass_vector.start_image_xy}
            end={tactic.calibration.pass_vector.end_image_xy}
            lengthM={tactic.calibration.pass_vector.length_m}
            angleDeg={tactic.calibration.pass_vector.angle_deg}
            mode="persisted"
          />
        )}

        {/* Click-capture overlay (calibration corners OR vector preview clicks OR pass-vector pins) */}
        {(phase === "placing" || phase === "depth_near" || phase === "depth_far" || phase === "vector" || phase === "pass_vector") && (
          <div
            ref={overlayRef}
            data-testid={`tactic-calib-overlay-${tactic.id}`}
            onClick={handleOverlayClick}
            className="absolute inset-0 cursor-crosshair"
            style={{ background: "rgba(0,0,0,0.0)" }}
          >
            {/* Calibration markers (during placing) */}
            {phase === "placing" && points.map((p, i) => (
              <div
                key={i}
                className="absolute -translate-x-1/2 -translate-y-1/2 flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground font-display font-bold text-xs border-2 border-background shadow-lg shadow-primary/40"
                style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%` }}
              >
                {i + 1}
              </div>
            ))}
            {/* Depth-line markers */}
            {(phase === "depth_near" || phase === "depth_far") && (() => {
              const arr = phase === "depth_near" ? nearLine : farLine;
              const color = phase === "depth_near" ? "#FFB400" : "#FF59B2";
              return (
                <>
                  {arr.map((p, i) => (
                    <div
                      key={`d${i}`}
                      className="absolute -translate-x-1/2 -translate-y-1/2 flex items-center justify-center w-6 h-6 rounded-full font-display font-bold text-xs border-2 border-background shadow-lg"
                      style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%`, background: color, color: "#001" }}
                    >
                      {i === 0 ? "G" : "B"}
                    </div>
                  ))}
                  {arr.length === 2 && overlaySize.w > 0 && (
                    <svg className="absolute inset-0 w-full h-full pointer-events-none">
                      <line
                        x1={arr[0].x * overlaySize.w} y1={arr[0].y * overlaySize.h}
                        x2={arr[1].x * overlaySize.w} y2={arr[1].y * overlaySize.h}
                        stroke={color} strokeWidth="3" strokeDasharray="6 4"
                      />
                    </svg>
                  )}
                  {/* Show 4-corner polygon faded as reference */}
                  {points.length === 4 && overlaySize.w > 0 && (
                    <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-40">
                      <polyline
                        points={points.map((p) => `${p.x * overlaySize.w},${p.y * overlaySize.h}`).join(" ")
                          + ` ${points[0].x * overlaySize.w},${points[0].y * overlaySize.h}`}
                        fill="rgba(108, 235, 130, 0.08)"
                        stroke="rgba(108, 235, 130, 0.7)"
                        strokeWidth="2"
                      />
                    </svg>
                  )}
                </>
              );
            })()}
            {/* Vector probe markers (during vector preview) */}
            {phase === "vector" && vectorPoints.map((v, i) => (
              <div
                key={i}
                className={`absolute -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full border-2 border-background shadow-lg ${v.inside ? "bg-emerald-400" : "bg-amber-400"}`}
                style={{ left: `${v.img.x * 100}%`, top: `${v.img.y * 100}%` }}
                title={`(${v.field.x.toFixed(1)}, ${v.field.y.toFixed(1)}) m`}
              />
            ))}
            {/* Current target label, top-left of overlay (calibration only) */}
            {phase === "placing" && currentStep && (
              <div className="absolute top-3 left-3 px-3 py-1.5 bg-card/90 backdrop-blur border border-primary/60 rounded-sm font-mono text-[11px] uppercase tracking-widest text-primary flex items-center gap-2">
                <span className="font-display font-bold text-base">{currentStep.idx + 1}</span>
                <span>Click {currentStep.label}</span>
              </div>
            )}
            {phase === "placing" && points.length === 4 && (
              <div className="absolute top-3 left-3 px-3 py-1.5 bg-card/90 backdrop-blur border border-primary rounded-sm font-mono text-[11px] uppercase tracking-widest text-primary flex items-center gap-2">
                <Check className="w-3.5 h-3.5" /> All 4 corners set — press SAVE
              </div>
            )}
            {phase === "vector" && (
              <div className="absolute top-3 left-3 px-3 py-1.5 bg-card/90 backdrop-blur border border-primary/60 rounded-sm font-mono text-[11px] uppercase tracking-widest text-primary flex items-center gap-2">
                <Activity className="w-3.5 h-3.5" /> Vector probe · click any field point
              </div>
            )}
            {/* Pass-vector hint label */}
            {phase === "pass_vector" && (
              <div
                data-testid={`tactic-pass-vector-hint-${tactic.id}`}
                className="absolute top-3 left-3 px-3 py-1.5 bg-card/90 backdrop-blur border border-pink-400/70 rounded-sm font-mono text-[11px] uppercase tracking-widest text-pink-300 flex items-center gap-2"
              >
                <ArrowRightCircle className="w-3.5 h-3.5" />
                {!passVecStart
                  ? "Click 1/2 · RELEASE (Werfer)"
                  : !passVecEnd
                    ? "Click 2/2 · CATCH (Empfänger)"
                    : passVecSaving
                      ? "Saving pass vector…"
                      : "Vector saved · click again to replace"}
              </div>
            )}
            {/* Pass-vector markers + live SVG arrow */}
            {phase === "pass_vector" && overlaySize.w > 0 && (
              <PassVectorOverlay
                start={passVecStart}
                end={passVecEnd}
                mode="editing"
              />
            )}
            {phase === "depth_near" && (
              <div className="absolute top-3 left-3 px-3 py-1.5 bg-card/95 backdrop-blur border border-amber-400 rounded-sm font-mono text-[11px] uppercase tracking-widest text-amber-400 flex items-center gap-2">
                <Ruler className="w-3.5 h-3.5" />
                Near depth · click {nearLine.length === 0 ? "GOAL-line end" : nearLine.length === 1 ? "BACK-line end" : "done — press apply"}
              </div>
            )}
            {phase === "depth_far" && (
              <div className="absolute top-3 left-3 px-3 py-1.5 bg-card/95 backdrop-blur border border-pink-400 rounded-sm font-mono text-[11px] uppercase tracking-widest text-pink-400 flex items-center gap-2">
                <Ruler className="w-3.5 h-3.5" />
                Far depth · click {farLine.length === 0 ? "GOAL-line end" : farLine.length === 1 ? "BACK-line end" : "done — press apply"}
              </div>
            )}
            {/* Calibration polygon during placing */}
            {phase === "placing" && points.length >= 2 && overlaySize.w > 0 && (
              <svg className="absolute inset-0 w-full h-full pointer-events-none">
                <polyline
                  points={points.map((p) => `${p.x * overlaySize.w},${p.y * overlaySize.h}`).join(" ")
                    + (points.length === 4 ? ` ${points[0].x * overlaySize.w},${points[0].y * overlaySize.h}` : "")}
                  fill={points.length === 4 ? "rgba(108, 235, 130, 0.12)" : "none"}
                  stroke="rgba(108, 235, 130, 0.85)"
                  strokeWidth="2"
                  strokeDasharray="6 4"
                />
              </svg>
            )}
          </div>
        )}
      </div>

      {/* Vector preview panel: top-down field with projected probe points */}
      {phase === "vector" && tactic.calibration && (
        <VectorPreviewPanel
          calibration={tactic.calibration}
          probes={vectorPoints}
          onUndo={undoVector}
          onClear={clearVector}
        />
      )}

      {/* Bottom control bar */}
      <div className="px-3 py-2.5 border-t border-border bg-card/30 space-y-2">
        {/* Frame scrubber */}
        {phase === "idle" && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Frame Scrubber</span>
            <Button size="sm" variant="outline" onClick={() => seekBy(-1)} className="h-7 px-2 font-mono text-[10px]" data-testid={`tactic-calib-back-1-${tactic.id}`}>
              <ChevronLeft className="w-3 h-3" /><ChevronLeft className="w-3 h-3 -ml-2" /> 1s
            </Button>
            <Button size="sm" variant="outline" onClick={() => seekBy(-0.1)} className="h-7 px-2 font-mono text-[10px]" data-testid={`tactic-calib-back-0.1-${tactic.id}`}>
              <ChevronLeft className="w-3 h-3" /> 0.1s
            </Button>
            <Button size="sm" variant="outline" onClick={captureCurrentTime} className="h-7 px-2 font-mono text-[10px]" data-testid={`tactic-calib-capture-${tactic.id}`}>
              Capture
            </Button>
            <Button size="sm" variant="outline" onClick={() => seekBy(0.1)} className="h-7 px-2 font-mono text-[10px]" data-testid={`tactic-calib-fwd-0.1-${tactic.id}`}>
              0.1s <ChevronRight className="w-3 h-3" />
            </Button>
            <Button size="sm" variant="outline" onClick={() => seekBy(1)} className="h-7 px-2 font-mono text-[10px]" data-testid={`tactic-calib-fwd-1-${tactic.id}`}>
              1s <ChevronRight className="w-3 h-3" /><ChevronRight className="w-3 h-3 -ml-2" />
            </Button>
            <span className="font-mono text-[10px] text-muted-foreground ml-1">
              · use YT timeline for coarse, buttons for precision
            </span>
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Field</span>
          <Select value={fieldType} onValueChange={setFieldType} disabled={phase !== "idle"}>
            <SelectTrigger data-testid={`tactic-calib-field-type-${tactic.id}`} className="h-7 w-56 bg-secondary border-border font-mono text-[11px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TACTIC_FIELD_PRESETS.map((f) => (
                <SelectItem key={f.id} value={f.id} className="font-mono text-xs">
                  <div className="flex items-center justify-between gap-3 w-full">
                    <span>{f.labelDe}</span>
                    <span className="text-[10px] text-muted-foreground tabular-nums">
                      {f.length}×{f.width} m
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="ml-auto flex items-center gap-1.5">
            {phase === "idle" && (
              <>
                {hasSaved && (
                  <>
                    <Button size="sm" variant="outline"
                      onClick={() => { setPassVecStart(null); setPassVecEnd(null); setPhase("pass_vector"); }}
                      data-testid={`tactic-pass-vector-start-${tactic.id}`}
                      className="h-7 px-2 border-pink-400/40 bg-pink-400/10 hover:bg-pink-400 hover:text-black text-pink-300 font-display font-bold uppercase tracking-widest text-[10px]">
                      <ArrowRightCircle className="w-3.5 h-3.5 mr-1" />
                      {tactic.calibration?.pass_vector ? "Re-Place Pass" : "+ Pass Vector"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setVectorPoints([]); setPhase("vector"); }}
                      data-testid={`tactic-vector-start-${tactic.id}`}
                      className="h-7 px-2 border-primary/40 bg-primary/10 hover:bg-primary hover:text-primary-foreground text-primary font-display font-bold uppercase tracking-widest text-[10px]">
                      <Activity className="w-3.5 h-3.5 mr-1" /> Vector Preview
                    </Button>
                  </>
                )}
                <Button size="sm" onClick={lockFrame}
                  data-testid={`tactic-calib-start-${tactic.id}`}
                  className="h-7 bg-primary text-primary-foreground hover:bg-primary/90 font-display font-bold uppercase tracking-widest text-[10px]">
                  <Crosshair className="w-3.5 h-3.5 mr-1" /> {hasSaved ? "Re-Calibrate" : "Calibrate at this Frame"}
                </Button>
              </>
            )}
            {phase === "vector" && (
              <Button size="sm" variant="outline" onClick={() => setPhase("idle")}
                data-testid={`tactic-vector-exit-${tactic.id}`}
                className="h-7 px-2 font-mono text-[10px] uppercase tracking-widest">
                <EyeOff className="w-3 h-3 mr-1" /> Exit Preview
              </Button>
            )}
            {phase === "pass_vector" && (
              <>
                <Button size="sm" variant="ghost" onClick={() => { setPassVecStart(null); setPassVecEnd(null); }}
                  disabled={!passVecStart || passVecSaving}
                  data-testid={`tactic-pass-vector-reset-${tactic.id}`}
                  className="h-7 px-2 font-mono text-[10px] uppercase tracking-widest hover:bg-accent hover:text-primary disabled:opacity-30">
                  <RotateCcw className="w-3 h-3 mr-1" /> Reset
                </Button>
                {tactic.calibration?.pass_vector && (
                  <Button size="sm" variant="ghost" onClick={clearPassVector}
                    disabled={passVecSaving}
                    data-testid={`tactic-pass-vector-clear-${tactic.id}`}
                    className="h-7 px-2 font-mono text-[10px] uppercase tracking-widest hover:bg-destructive/20 hover:text-destructive disabled:opacity-30">
                    <Trash2 className="w-3 h-3 mr-1" /> Delete
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setPhase("idle")}
                  disabled={passVecSaving}
                  data-testid={`tactic-pass-vector-exit-${tactic.id}`}
                  className="h-7 px-2 font-mono text-[10px] uppercase tracking-widest">
                  <XIcon className="w-3 h-3 mr-1" /> Done
                </Button>
              </>
            )}
            {phase === "placing" && (
              <>
                <Button size="sm" variant="ghost" onClick={undoPoint} disabled={points.length === 0}
                  data-testid={`tactic-calib-undo-${tactic.id}`}
                  className="h-7 px-2 font-mono text-[10px] uppercase tracking-widest hover:bg-accent hover:text-primary disabled:opacity-30">
                  <RotateCcw className="w-3 h-3 mr-1" /> Undo
                </Button>
                <Button size="sm" variant="ghost" onClick={clearPoints} disabled={points.length === 0}
                  className="h-7 px-2 font-mono text-[10px] uppercase tracking-widest hover:bg-accent hover:text-primary disabled:opacity-30">
                  Clear
                </Button>
                {points.length === 4 && (
                  <>
                    <Button size="sm" variant="outline"
                      onClick={() => setPhase("depth_near")}
                      data-testid={`tactic-calib-near-${tactic.id}`}
                      className={`h-7 px-2 font-mono text-[10px] uppercase tracking-widest border-amber-400/60 ${nearLine.length === 2 ? "bg-amber-400/20 text-amber-300" : "text-amber-400 hover:bg-amber-400/15"}`}>
                      <Ruler className="w-3 h-3 mr-1" /> + Near {nearLine.length === 2 ? "✓" : ""}
                    </Button>
                    <Button size="sm" variant="outline"
                      onClick={() => setPhase("depth_far")}
                      data-testid={`tactic-calib-far-${tactic.id}`}
                      className={`h-7 px-2 font-mono text-[10px] uppercase tracking-widest border-pink-400/60 ${farLine.length === 2 ? "bg-pink-400/20 text-pink-300" : "text-pink-400 hover:bg-pink-400/15"}`}>
                      <Ruler className="w-3 h-3 mr-1" /> + Far {farLine.length === 2 ? "✓" : ""}
                    </Button>
                  </>
                )}
                <Button size="sm" variant="outline" onClick={unlockFrame}
                  className="h-7 px-2 font-mono text-[10px] uppercase tracking-widest">
                  <XIcon className="w-3 h-3 mr-1" /> Cancel
                </Button>
                <Button size="sm" onClick={onSave} disabled={!canSave}
                  data-testid={`tactic-calib-save-${tactic.id}`}
                  className="h-7 bg-primary text-primary-foreground hover:bg-primary/90 font-display font-bold uppercase tracking-widest text-[10px] disabled:opacity-40">
                  <Save className="w-3.5 h-3.5 mr-1" /> Save ({points.length}/4)
                </Button>
              </>
            )}
            {(phase === "depth_near" || phase === "depth_far") && (() => {
              const isNear = phase === "depth_near";
              const arr = isNear ? nearLine : farLine;
              const setArr = isNear ? setNearLine : setFarLine;
              const meters = isNear ? nearMeters : farMeters;
              const setMeters = isNear ? setNearMeters : setFarMeters;
              return (
                <>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">m</span>
                  <Input type="number" step="0.1" min="0.1" max="50"
                    value={meters}
                    onChange={(e) => setMeters(parseFloat(e.target.value) || 0)}
                    className="h-7 w-16 bg-secondary border-border font-mono text-[11px]"
                    data-testid={`tactic-depth-${phase}-meters-${tactic.id}`} />
                  <Button size="sm" variant="ghost" onClick={() => setArr([])} disabled={arr.length === 0}
                    className="h-7 px-2 font-mono text-[10px] uppercase tracking-widest hover:bg-accent disabled:opacity-30">
                    <RotateCcw className="w-3 h-3 mr-1" /> Reset
                  </Button>
                  <Button size="sm" onClick={() => setPhase("placing")}
                    data-testid={`tactic-depth-${phase}-apply-${tactic.id}`}
                    disabled={arr.length !== 2 || meters <= 0}
                    className="h-7 bg-primary text-primary-foreground hover:bg-primary/90 font-display font-bold uppercase tracking-widest text-[10px] disabled:opacity-40">
                    <Check className="w-3.5 h-3.5 mr-1" /> Apply
                  </Button>
                </>
              );
            })()}
          </div>
        </div>

        {phase === "placing" && (
          <div className="font-mono text-[10px] text-muted-foreground leading-relaxed">
            Click the field corners in this exact order on the paused frame:
            <span className="text-primary ml-1">{STEPS.map((s, i) => `${i + 1}.${s.short}`).join(" → ")}</span>.
            The polygon updates live so you can verify the shape before saving.
          </div>
        )}
        {phase === "vector" && (
          <div className="font-mono text-[10px] text-muted-foreground leading-relaxed">
            <span className="text-primary">Sanity check:</span> click any point on the field (a player, the disc, a line crossing) — the projected position appears live on the mini top-down map below. Green dot = inside the field rectangle, amber = outside (suggests miscalibration).
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Homography & top-down preview ----------
function applyHomography(H, x, y) {
  // H is a 3x3 matrix mapping normalised image coords → field metres.
  const a = H[0]; const b = H[1]; const c = H[2];
  const nx = a[0] * x + a[1] * y + a[2];
  const ny = b[0] * x + b[1] * y + b[2];
  const w = c[0] * x + c[1] * y + c[2];
  return { x: w !== 0 ? nx / w : 0, y: w !== 0 ? ny / w : 0 };
}

/**
 * Pass-vector overlay (release → catch arrow) shown on top of the YouTube
 * iframe. Used in two modes:
 *   "editing"   — live during click placement. Renders pin 1 then pin 2,
 *                 plus a dashed arrow once both are set.
 *   "persisted" — read-only view of a saved pass_vector with length + angle
 *                 callout for the coach. Hidden tooltip on the catch pin.
 *
 * Coordinates are normalised [0..1] of the iframe surface — the parent's
 * absolute layout takes care of pixel placement. SVG arrowhead is built
 * with a defs `<marker>` so it scales with the line.
 */
function PassVectorOverlay({ start, end, lengthM, angleDeg, mode = "editing" }) {
  // Unique marker id (declared BEFORE any early returns to satisfy the
  // rules-of-hooks contract — useMemo must run on every render).
  const mid = useMemo(
    () => `pv-arrow-${Math.random().toString(36).slice(2, 9)}`,
    [],
  );
  if (!start && !end) return null;
  const editing = mode === "editing";
  return (
    <div className="absolute inset-0 pointer-events-none" data-testid="pass-vector-overlay">
      <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none" viewBox="0 0 100 100">
        <defs>
          <marker id={mid} viewBox="0 0 12 12" refX="10" refY="6"
                  markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M 0 0 L 12 6 L 0 12 L 3 6 Z" fill="#FF59B2" />
          </marker>
        </defs>
        {start && end && (
          <>
            {/* Glow underlay */}
            <line
              x1={start.x * 100} y1={start.y * 100}
              x2={end.x * 100}   y2={end.y * 100}
              stroke="rgba(255,89,178,0.18)" strokeWidth="3.0"
              strokeLinecap="round" vectorEffect="non-scaling-stroke"
            />
            <line
              x1={start.x * 100} y1={start.y * 100}
              x2={end.x * 100}   y2={end.y * 100}
              stroke="#FF59B2"
              strokeWidth={editing ? "0.7" : "0.55"}
              strokeDasharray={editing ? "1.6 1.0" : ""}
              strokeLinecap="round"
              markerEnd={`url(#${mid})`}
              vectorEffect="non-scaling-stroke"
            />
          </>
        )}
      </svg>
      {/* Release pin */}
      {start && (
        <div
          data-testid="pass-vector-pin-start"
          className="absolute -translate-x-1/2 -translate-y-1/2 flex items-center justify-center w-7 h-7 rounded-full bg-pink-400 text-black font-display font-black text-[11px] border-2 border-background shadow-lg shadow-pink-400/40"
          style={{ left: `${start.x * 100}%`, top: `${start.y * 100}%` }}
        >R</div>
      )}
      {/* Catch pin */}
      {end && (
        <div
          data-testid="pass-vector-pin-end"
          className="absolute -translate-x-1/2 -translate-y-1/2 flex items-center justify-center w-7 h-7 rounded-full bg-pink-300 text-black font-display font-black text-[11px] border-2 border-background shadow-lg shadow-pink-400/40"
          style={{ left: `${end.x * 100}%`, top: `${end.y * 100}%` }}
        >C</div>
      )}
      {/* Length / angle callout (persisted mode only — editing mode shows
          the hint label instead) */}
      {!editing && lengthM != null && end && (
        <div
          data-testid="pass-vector-length-tag"
          className="absolute px-2 py-0.5 bg-card/95 border border-pink-400 rounded-sm font-mono text-[10px] uppercase tracking-widest text-pink-300 whitespace-nowrap"
          style={{
            left: `${((start.x + end.x) / 2) * 100}%`,
            top: `${((start.y + end.y) / 2) * 100}%`,
            transform: "translate(-50%, -120%)",
          }}
        >
          {lengthM.toFixed(1)} m{angleDeg != null ? ` · ${Math.round(angleDeg)}°` : ""}
        </div>
      )}
    </div>
  );
}

function VectorPreviewPanel({ calibration, probes, onUndo, onClear }) {
  const L = calibration.rect_length_m || 100;
  const W = calibration.rect_width_m || 37;
  const PAD = 6;          // metre padding around the rect for visual breathing room
  const vbW = L + PAD * 2;
  const vbH = W + PAD * 2;

  return (
    <div className="border-t border-border bg-card/40 p-3 grid lg:grid-cols-3 gap-3">
      <div className="lg:col-span-2">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5 flex items-center gap-2">
          <Activity className="w-3 h-3 text-primary" />
          Top-Down Projection · {getPreset("Outdoor", calibration.field_type).labelDe} · {L}×{W} m
        </div>
        <div className="bg-emerald-950/30 border border-border rounded-sm overflow-hidden">
          <svg viewBox={`0 0 ${vbW} ${vbH}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
            {/* Outer field rect */}
            <rect x={PAD} y={PAD} width={L} height={W} fill="rgba(56,189,124,0.10)" stroke="rgba(108,235,130,0.7)" strokeWidth="0.25" />
            {/* Endzones (only for hauptspielfeld: 18m at each end) */}
            {calibration.field_type === "hauptspielfeld" && (
              <>
                <rect x={PAD} y={PAD} width={18} height={W} fill="rgba(108,235,130,0.06)" stroke="rgba(108,235,130,0.5)" strokeWidth="0.2" strokeDasharray="0.5,0.5" />
                <rect x={PAD + L - 18} y={PAD} width={18} height={W} fill="rgba(108,235,130,0.06)" stroke="rgba(108,235,130,0.5)" strokeWidth="0.2" strokeDasharray="0.5,0.5" />
                {/* Midline */}
                <line x1={PAD + L / 2} y1={PAD} x2={PAD + L / 2} y2={PAD + W} stroke="rgba(108,235,130,0.35)" strokeWidth="0.15" strokeDasharray="0.4,0.4" />
              </>
            )}
            {/* Corner labels */}
            <text x={PAD - 2} y={PAD - 1.5} fontSize="2.5" fill="rgba(255,255,255,0.4)" textAnchor="end">TL</text>
            <text x={PAD + L + 2} y={PAD - 1.5} fontSize="2.5" fill="rgba(255,255,255,0.4)">TR</text>
            <text x={PAD + L + 2} y={PAD + W + 3.5} fontSize="2.5" fill="rgba(255,255,255,0.4)">BR</text>
            <text x={PAD - 2} y={PAD + W + 3.5} fontSize="2.5" fill="rgba(255,255,255,0.4)" textAnchor="end">BL</text>
            {/* Trajectory line connecting consecutive probes */}
            {probes.length >= 2 && (
              <polyline
                points={probes.map((p) => `${PAD + p.field.x},${PAD + p.field.y}`).join(" ")}
                fill="none"
                stroke="rgba(108,235,130,0.65)"
                strokeWidth="0.25"
                strokeDasharray="0.8,0.6"
              />
            )}
            {/* Probe markers */}
            {probes.map((p, i) => (
              <g key={i}>
                <circle cx={PAD + p.field.x} cy={PAD + p.field.y} r="0.9"
                  fill={p.inside ? "rgba(108,235,130,0.95)" : "rgba(251,191,36,0.95)"}
                  stroke="black" strokeWidth="0.15" />
                <text x={PAD + p.field.x + 1.2} y={PAD + p.field.y + 0.5} fontSize="2" fill="rgba(255,255,255,0.85)">{i + 1}</text>
              </g>
            ))}
          </svg>
        </div>
      </div>
      <div className="space-y-2">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Probes · {probes.length}</div>
        <div className="max-h-44 overflow-y-auto border border-border bg-secondary rounded-sm">
          {probes.length === 0 ? (
            <div className="p-3 font-mono text-[10px] text-muted-foreground">Click on the field in the player above…</div>
          ) : (
            <table className="w-full font-mono text-[10px]">
              <thead className="border-b border-border bg-card text-muted-foreground uppercase tracking-widest">
                <tr><th className="text-left py-1 px-2">#</th><th className="text-left">X (m)</th><th className="text-left">Y (m)</th><th></th></tr>
              </thead>
              <tbody>
                {probes.map((p, i) => (
                  <tr key={i} className="border-b border-border/40 last:border-b-0">
                    <td className="py-1 px-2 text-primary">{i + 1}</td>
                    <td className={p.inside ? "text-foreground" : "text-amber-400"}>{p.field.x.toFixed(2)}</td>
                    <td className={p.inside ? "text-foreground" : "text-amber-400"}>{p.field.y.toFixed(2)}</td>
                    <td>{p.inside ? "" : "⚠"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="flex gap-1.5">
          <Button size="sm" variant="ghost" onClick={onUndo} disabled={probes.length === 0}
            className="flex-1 h-7 px-2 font-mono text-[10px] uppercase tracking-widest hover:bg-accent hover:text-primary disabled:opacity-30">
            <RotateCcw className="w-3 h-3 mr-1" /> Undo
          </Button>
          <Button size="sm" variant="ghost" onClick={onClear} disabled={probes.length === 0}
            className="flex-1 h-7 px-2 font-mono text-[10px] uppercase tracking-widest hover:bg-accent hover:text-primary disabled:opacity-30">
            Clear
          </Button>
        </div>
      </div>
    </div>
  );
}

function fmt(s) {
  if (s == null) return "—";
  const total = Math.floor(s);
  const ms = Math.floor((s - total) * 10);
  const m = Math.floor(total / 60);
  const sec = (total % 60).toString().padStart(2, "0");
  return `${m}:${sec}.${ms}`;
}

function round4(v) { return Math.round(v * 10000) / 10000; }

/**
 * Draggable brick reticle for a saved YT tactic calibration.
 *
 * UX (2D-Drag): hover → grab cursor; mouse-down → grabbing; while dragging
 * the crosshair follows the cursor freely in 2D and a floating tag shows
 * the live real-world (X, Y) in metres (derived via H_image_to_world).
 * On release we POST the cursor position as `brick_image_xy` override so
 * the backend persists it verbatim AND emits `brick_field_xy` — the true
 * 2D anchor used by the Density Heatmap. No more snap-back to the
 * centerline; the admin can pin any tactical play anywhere on the field.
 */
function BrickReticleDrag({ tactic, onSaved }) {
  const cal = tactic.calibration;
  const overlayRef = useRef(null);
  const [drag, setDrag] = useState(null);     // {x, y} normalised cursor pos during drag
  const [liveField, setLiveField] = useState({ x: cal.brick_field_xy?.x ?? 0, y: cal.brick_field_xy?.y ?? 0 });
  // Mirror the latest drag pixel + field-xy in refs so the mouse-up
  // closure (defined at mouse-down time) reads the FINAL drag values, not
  // the stale ones captured at the start of the gesture.
  const dragRef = useRef(null);
  const liveFieldRef = useRef(liveField);
  useEffect(() => { liveFieldRef.current = liveField; }, [liveField]);
  const side = cal.brick_side || "near";
  const L = cal.rect_length_m || 18;
  const H = cal.homography_matrix;

  const applyH = (Hm, x, y) => {
    const w = Hm[2][0] * x + Hm[2][1] * y + Hm[2][2];
    return [
      (Hm[0][0] * x + Hm[0][1] * y + Hm[0][2]) / w,
      (Hm[1][0] * x + Hm[1][1] * y + Hm[1][2]) / w,
    ];
  };

  const startDrag = (e) => {
    e.preventDefault();
    if (!H) { toast.error("No homography on this tactic — re-calibrate first"); return; }
    const ringEl = overlayRef.current?.parentElement; // .relative.aspect-video wrapper
    if (!ringEl) return;
    const rect = ringEl.getBoundingClientRect();
    const move = (ev) => {
      const x = (ev.clientX - rect.left) / rect.width;
      const y = (ev.clientY - rect.top) / rect.height;
      if (x < 0 || x > 1 || y < 0 || y > 1) return;
      setDrag({ x, y });
      dragRef.current = { x, y };
      const [wx, wy] = applyH(H, x, y);
      setLiveField({ x: wx, y: wy });
    };
    const up = async () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      setDrag(null);
      const finalPx = dragRef.current;
      if (!finalPx) return;
      const finalField = liveFieldRef.current;
      // 1D legacy fallback for display: derive distance from y-coord.
      const finalMeters = Math.max(0,
        +((side === "far" ? finalField.y - L : -finalField.y).toFixed(1)));
      // Persist 2D-Drag: send `brick_image_xy` override so the backend
      // anchors the brick at the exact cursor pixel and recomputes
      // `brick_field_xy` from H. Everything else stays.
      try {
        const updated = await axiosApi.post(`/tactics/${tactic.id}/calibrate`, {
          image_points: cal.image_points,
          calibration_frame_timestamp: cal.calibration_frame_timestamp,
          field_type: cal.field_type,
          depth_lines: cal.depth_lines || [],
          brick_distance_m: finalMeters,
          brick_side: side,
          brick_image_xy: { x: finalPx.x, y: finalPx.y },
        }).then((r) => r.data);
        toast.success(
          `Brick anchored at (${finalField.x.toFixed(1)}, ${finalField.y.toFixed(1)}) m`,
        );
        onSaved?.(updated);
      } catch (err) {
        toast.error(fehlerText(err) || "Brick update failed");
      }
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  // Display position: drag cursor if active, else the persisted canonical XY.
  const pos = drag || cal.brick_image_xy || { x: 0.5, y: 0.5 };
  // Tag display: live (X, Y) while dragging; otherwise persisted brick_field_xy.
  const tagX = drag ? liveField.x : (cal.brick_field_xy?.x);
  const tagY = drag ? liveField.y : (cal.brick_field_xy?.y);

  return (
    <div
      ref={overlayRef}
      data-testid={`tactic-brick-reticle-${tactic.id}`}
      onMouseDown={startDrag}
      className="absolute select-none group"
      title={`Drag the brick reticle anywhere on the field — releases pin it`}
      style={{
        left: `${pos.x * 100}%`,
        top: `${pos.y * 100}%`,
        width: 56, height: 56, marginLeft: -28, marginTop: -28,
        cursor: drag ? "grabbing" : "grab",
        touchAction: "none",
      }}
    >
      <svg viewBox="0 0 56 56" className="absolute inset-0 pointer-events-none">
        <circle cx="28" cy="28" r="14" fill="none" stroke="#FF59B2" strokeWidth="2" />
        <line x1="28" y1="6" x2="28" y2="18" stroke="#FF59B2" strokeWidth="2" />
        <line x1="28" y1="38" x2="28" y2="50" stroke="#FF59B2" strokeWidth="2" />
        <line x1="6" y1="28" x2="18" y2="28" stroke="#FF59B2" strokeWidth="2" />
        <line x1="38" y1="28" x2="50" y2="28" stroke="#FF59B2" strokeWidth="2" />
        <circle cx="28" cy="28" r="2" fill="#FF59B2" />
      </svg>
      <div
        data-testid={`tactic-brick-tag-${tactic.id}`}
        className={`absolute top-full left-1/2 -translate-x-1/2 mt-1 px-2 py-0.5 bg-card/95 border border-pink-400 rounded-sm font-mono text-[10px] uppercase tracking-widest text-pink-300 whitespace-nowrap pointer-events-none transition-opacity ${drag ? "opacity-100" : "opacity-0 group-hover:opacity-90"}`}
      >
        {drag ? "→ " : "↔ "}
        {tagX != null && tagY != null
          ? `(${tagX.toFixed(1)}, ${tagY.toFixed(1)}) m`
          : `${side}`}
      </div>
    </div>
  );
}
